import React, {useState, useEffect, useRef, useCallback} from 'react';
import {useIntl} from 'react-intl';
import {connect} from 'react-redux';
import {Layout, message, Spin, Drawer} from 'antd';
import {MenuOutlined} from "@ant-design/icons";
import {changeSidebarCollapsed} from 'store/actions/sidebarCollapsed';
import {clearSession, setSession, toggleSessionLoading, setSessionSelectedOrganization} from 'store/actions/session';
import {switchToLearningEnvironment, switchToAdministrationEnvironment} from 'store/actions/environment';
import {NavLink, useNavigate} from 'react-router-dom';
import Spinner from 'components/Spinner';
import Router from 'components/Router';
import Sidebar from 'components/Sidebar';
import HeaderMenu from 'components/HeaderMenu';
import PageLayout from './PageLayout';
import PageContent from './PageContent';
import PageSidebar from './PageSidebar';
import PageHeader from './PageHeader';
import PageFooter from './PageFooterWithRow';
import jwt from 'utils/jwt';
import apiClient from 'utils/apiClient';
import {config} from 'config/config';
import {VideoInstruction} from 'components/VideoInstructions';
import {loadAppearanceSettings} from 'store/actions/appearanceSettingsActions';
import { loadLanguageDictionary, readCachedLanguageDictionary } from 'store/actions/languageDictionary';
import {AppearanceSettings, AppearanceSettingsData} from 'interfaces/redux';
import Helmet from 'react-helmet';
import {ThemeSwitch} from 'ui';
import Cookies from 'js-cookie';
import './styles.scss';
import {isMobile} from 'react-device-detect';
import StudentNavBar from "../StudentNavBar";
import Confirmation from "pages/Rules/Confirmation";
import { useLocaleContext } from 'context/LocaleContext';
import useQueryApiClient from "utils/useApiClient";
import { useTableColumnsContext } from 'context/TableColumnsContext';

const {Header, Content} = Layout;

const mapStateToProps = (state: any) => ({
    environment: state.environment.environment,
    session: state.session,
    languageDictionary: state.languageDictionary,
    appearanceSettings: state.appearanceSettings,
    appearanceSettingsLoading: state.appearanceSettings.loading,
    logoPath: state.appearanceSettings.logoPath,
    title: state.appearanceSettings.title,
});

const mapDispatchToProps = (dispatch: any) => ({
    changeSidebarCollapsed: (sidebarCollapsed: boolean) => dispatch(changeSidebarCollapsed(sidebarCollapsed)),
    setSession: (session: any) => dispatch(setSession(session)),
    setSessionSelectedOrganization: (organization: any) => dispatch(setSessionSelectedOrganization(organization)),
    clearSession: () => dispatch(clearSession()),
    toggleSessionLoading: () => dispatch(toggleSessionLoading()),
    switchToLearningEnvironment: () => dispatch(switchToLearningEnvironment()),
    switchToAdministrationEnvironment: () => dispatch(switchToAdministrationEnvironment()),
    loadAppearanceSettings: (data: AppearanceSettingsData) => dispatch(loadAppearanceSettings(data)),
    loadFullLanguageDictionary: () => dispatch(loadLanguageDictionary('full')),
});

interface DefaultLayoutProps {
    session: any;
    changeSidebarCollapsed: Function;
    setSession: Function;
    setSessionSelectedOrganization: Function;
    clearSession: Function;
    toggleSessionLoading: Function;
    environment: string;
    switchToLearningEnvironment: Function;
    switchToAdministrationEnvironment: Function;
    languageDictionary: any;
    loadAppearanceSettings: Function;
    loadFullLanguageDictionary: () => void;
    appearanceSettings: AppearanceSettings;
    appearanceSettingsLoading: boolean;
    logoPath: any;
    title: string;
}

const DefaultLayout = ({
    session,
    changeSidebarCollapsed,
    setSession,
    setSessionSelectedOrganization,
    clearSession,
    toggleSessionLoading,
    environment,
    switchToLearningEnvironment,
    switchToAdministrationEnvironment,
    languageDictionary,
    loadAppearanceSettings,
    loadFullLanguageDictionary,
    appearanceSettings,
    appearanceSettingsLoading,
    logoPath,
    title
}: DefaultLayoutProps) => {
    const { locale, setLocale } = useLocaleContext();
    const { setDisplayColumns } = useTableColumnsContext();

    const [collapsed, setCollapsed] = useState(false);
    const [selectedOrganization, setSelectedOrganization] = useState<any>(null);
    const [selectedOrganizationType, setSelectedOrganizationType] = useState<any>(null);
    const [selectedUserTypeId, setSelectedUserTypeId] = useState<any>(null);
    const [roles, setRoles] = useState<any>([]);
    const [favicon, setFavicon] = useState();
    const [logo, setLogo] = useState();
    const [logoIsLoading, setLogoIsLoading] = useState(false);
    const [code, setCode] = useState();
    const [showVideoInstructionModal, setShowVideoInstructionModal] = useState(false);
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [showRulesModal, setShowRulesModal] = useState(false)
    const currentTokenRef = useRef<string | undefined>(jwt.get());
    const lastTokenChangeRef = useRef<string | null>(null);
    const lastFocusCheckRef = useRef<number>(0);


    useEffect(() => {
        const authMe = async () => {
            try {
                loadAppearanceSettings({});
                loadFullLanguageDictionary();
                const response = await apiClient.request(config.api.routes.auth.me);

                if (!!response.data.firstLogin && response.data.active_user_type === 'CUSTOMER_ADMIN') {
                    displayIntroVideo();
                }

                const sessionOrg: any = getSessionOrganization(response.data);

                if (
                    window.location.pathname !== '/student/show-activity' &&
                    response.data.active_user_type === 'STUDENT'
                ) {
                    setShowRulesModal(true);
                }

                const sessionValues = {
                    ...response.data,
                    organization: sessionOrg,
                };

                if (locale !== localStorage.getItem('locale')) {
                    setLocale(localStorage.getItem('locale') ?? sessionValues.language);
                }

                await setSession(sessionValues);

                if (response.data.active_user_type === 'SUPERVISION') {
                    navigate('/my-department');
                } else if (response.data.active_user_type === 'LECTOR') {
                    navigate('/my-lectures');
                }
            } catch (err: any) {
                if (err.status_code === 401 && err.message === "The token has been blacklisted") {
                    if (Cookies.get('jwt')) {
                        Cookies.remove('jwt');
                        navigate('/')
                    }
                }
                toggleSessionLoading();
            }
        };

        authMe().then(() => {
            currentTokenRef.current = jwt.get();
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const refreshSessionFromTokenChange = useCallback(async () => {
        const currentToken = jwt.get();

        // A token change can also mean the token is gone — another tab logged out. Without this the
        // handler returned silently and this tab kept showing an authenticated UI.
        if (!currentToken) {
            currentTokenRef.current = undefined;
            window.location.assign('/login');

            return;
        }

        if (currentToken !== currentTokenRef.current) {
            currentTokenRef.current = currentToken;

            try {
                sessionStorage.removeItem('current');
                loadAppearanceSettings({});
                const response = await apiClient.request(config.api.routes.auth.me);

                const sessionOrg: any = getSessionOrganization(response.data);

                const sessionValues = {
                    ...response.data,
                    organization: sessionOrg,
                };

                await setSession(sessionValues);

                setTimeout(() => {
                    const translations = readCachedLanguageDictionary();
                    if (translations) {
                        try {
                            const availableLocales = Object.keys(translations).filter(
                                (key) => key !== 'loading' && key !== 'failed'
                            );

                            if (availableLocales.length > 0 && !availableLocales.includes(locale)) {
                                const defaultLocale = availableLocales.includes(config.defaultEnvironmentLocale)
                                    ? config.defaultEnvironmentLocale
                                    : availableLocales[0];
                                setLocale(defaultLocale);
                            }
                        } catch (error) {
                            console.error('Failed to parse translations:', error);
                        }
                    }
                }, 500);

                if (
                    response.data.active_user_type === 'STUDENT' ||
                    response.data.active_user_type === 'LECTOR' ||
                    response.data.active_user_type === 'SUPERVISION'
                ) {
                    switchToLearningEnvironment();
                } else {
                    switchToAdministrationEnvironment();
                }
            } catch (err: any) {
                console.error('Failed to refresh session after token change:', err);
            }
        }
    }, [
        setSession,
        loadAppearanceSettings,
        switchToLearningEnvironment,
        switchToAdministrationEnvironment,
        locale,
        setLocale,
    ]);

    useEffect(() => {
        const handleStorageChange = async (e: StorageEvent) => {
            if (e.key === 'jwt_token_changed' && e.newValue) {
                const newTokenChangeTime = e.newValue;

                if (lastTokenChangeRef.current === newTokenChangeTime) {
                    return;
                }

                lastTokenChangeRef.current = newTokenChangeTime;
                await refreshSessionFromTokenChange();
            }
        };

        const handleFocus = async () => {
            const now = Date.now();
            if (now - lastFocusCheckRef.current < 1000) {
                return;
            }
            lastFocusCheckRef.current = now;

            const lastChangeTime = localStorage.getItem('jwt_token_changed');
            if (lastChangeTime && lastChangeTime !== lastTokenChangeRef.current) {
                lastTokenChangeRef.current = lastChangeTime;
                await refreshSessionFromTokenChange();
            }
        };

        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('focus', handleFocus);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('focus', handleFocus);
        };
    }, [refreshSessionFromTokenChange]);

    useQueryApiClient({
        request: {
            url: '/api/v1/environment/tables/values',
        },
        onSuccess: (response): void => {
            if (response?.data) {
                setDisplayColumns(response.data);
            }
        }
    });

    const displayIntroVideo = async () => {
        const response = await apiClient.request(`/api/v1/instruction/get/INTRO_INSTRUCTION/${locale}`, {}, 'GET');

        if (response.code.length > 0) {
            setCode(response.code[0].code);

            setShowVideoInstructionModal(true);

            await apiClient.request('/api/v1/auth/intro-log', [], 'POST');
        }
    };

    useEffect(() => {
        changeSidebarCollapsed(collapsed);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [collapsed]);

    useEffect(() => {
        const loadFavicon = async () => {
            try {
                const response = await apiClient.request(`api/v1${appearanceSettings.faviconPath}`, {}, 'GET', true, true);

                setFavicon(response);
            } catch (error) {
                console.error(error);
                message.error(intl.formatMessage({id: 'error.data_load'}));
            }
        };

        if (appearanceSettings.faviconPath) {
            loadFavicon();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appearanceSettings.faviconPath]);

    useEffect(() => {
        if (session.active_user_type !== null) {
            if (session.active_user_type === 'STUDENT' || session.active_user_type === 'LECTOR' || session.active_user_type === 'SUPERVISION') {
                switchToLearningEnvironment();
            } else {
                switchToAdministrationEnvironment();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [session && session.active_user_type_id])


    const getSessionOrganization = (session: any) => {
        let roles: any = [];

        let organization = {};

        let activeCustomerObj = Object.entries(session.user_types.customers || {})
            .map((el: any) => ({value: el[0], roles: el[1].roles}))
            .find((el: any) => el.roles[session.active_user_type] && el.roles[session.active_user_type].userTypeId === parseInt(session.active_user_type_id));

        let activeOrganizationObj = Object.entries(session.user_types.organizations || {})
            .map((el: any) => ({value: el[0], roles: el[1].roles}))
            .find((el: any) => el.roles[session.active_user_type] && el.roles[session.active_user_type].userTypeId === parseInt(session.active_user_type_id));


        if (activeCustomerObj) {
            roles = Object.entries(activeCustomerObj.roles).map((el: any) => ({
                userTypeId: el[1].userTypeId ? el[1].userTypeId : null,
                role: el[0]
            }));
            setSelectedOrganization(`c${activeCustomerObj.value}`);

            organization = {
                organization: 'CUSTOMER',
                organization_type: 'CUSTOMER',
                organization_id: activeCustomerObj.value,
                organization_uuid: session.user_types.customers[activeCustomerObj.value].uuid
            };

        } else if (activeOrganizationObj) {
            roles = Object.entries(activeOrganizationObj.roles).map((el: any) => ({
                userTypeId: el[1].userTypeId ? el[1].userTypeId : null,
                role: el[0]
            }));
            setSelectedOrganization(`o${activeOrganizationObj.value}`);

            let organizationType

            switch (session.active_user_type) {
                case 'RESELLER_ADMIN':
                    organizationType = 'RESELLER'
                    break;
                case 'DISTRIBUTOR_ADMIN':
                    organizationType = 'DISTRIBUTOR'
                    break;
            }
            organization = {
                organization: organizationType,
                organization_type: "ORGANIZATION",
                organization_id: activeOrganizationObj.value,
                organization_uuid: session.user_types.organizations[activeOrganizationObj.value].uuid
            };
        }

        if (session.user_types.hasOwnProperty('superAdmin')) {
            roles.push({userTypeId: session.user_types.superAdmin, role: 'SUPER_ADMIN'});
        }

        setRoles(roles);

        return organization;
    }

    // Priority order of the role to assume in the organization being switched to.
    const organizationSwitchRolePriority = [
        'RESELLER_ADMIN',
        'DISTRIBUTOR_ADMIN',
        'CUSTOMER_ADMIN',
        'LECTOR',
        'SUPERVISION',
        'STUDENT',
    ];

    useEffect(() => {
        if (!selectedOrganization || !selectedOrganizationType) {
            return;
        }

        const userTypes = session.user_types || {};
        const selectedOrganizationObject = userTypes[selectedOrganizationType]?.[selectedOrganization.substr(1)];

        const targetRole = selectedOrganizationObject
            ? organizationSwitchRolePriority.find((role: string) => selectedOrganizationObject.roles?.[role])
            : undefined;

        // The switch label renders from local state, so it already shows the new organization by the
        // time we get here. Previously an unknown organization — or one carrying none of the roles
        // above — fell through this block doing nothing at all: no token switch, no reload, no error,
        // leaving the header claiming a switch that never happened. Reload to resync the header with
        // the session that is actually in force.
        if (!targetRole) {
            message.error(
                intl.formatMessage({
                    id: 'error.switch_to_user_role_failed',
                    defaultMessage: 'Switching the organization failed.',
                })
            );
            window.location.reload();

            return;
        }

        sessionStorage.removeItem('current');
        sessionStorage.removeItem('translations');
        toggleSessionLoading();

        // switchUserRole reports its own business errors, but an outright rejection used to be an
        // unhandled promise, leaving the spinner up forever.
        Promise.resolve(jwt.switchUserRole(selectedOrganizationObject.roles[targetRole].userTypeId)).catch(
            (error: any) => {
                console.error(error);
                message.error(
                    intl.formatMessage({
                        id: 'error.switch_to_user_role_failed',
                        defaultMessage: 'Switching the organization failed.',
                    })
                );
                window.location.reload();
            }
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedOrganization, selectedOrganizationType])

    useEffect(() => {
        if (selectedUserTypeId && selectedUserTypeId !== session.active_user_type_id) {
            toggleSessionLoading();
            jwt.switchUserRole(selectedUserTypeId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedUserTypeId])

    useEffect(() => {
        const validateLocale = () => {
            const translations = readCachedLanguageDictionary();
            if (translations) {
                try {
                    const availableLocales = Object.keys(translations).filter(
                        (key) => key !== 'loading' && key !== 'failed'
                    );

                    if (availableLocales.length > 0 && !availableLocales.includes(locale)) {
                        const defaultLocale = availableLocales.includes(config.defaultEnvironmentLocale)
                            ? config.defaultEnvironmentLocale
                            : availableLocales[0];
                        setLocale(defaultLocale);
                    }
                } catch (error) {
                    console.error('Failed to parse translations:', error);
                }
            }
        };

        const timeoutId = setTimeout(() => {
            validateLocale();
        }, 500);

        return () => {
            clearTimeout(timeoutId);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [session?.organization, session?.active_user_type_id])

    useEffect(() => {
        const loadLogo = async () => {
            try {
                setLogoIsLoading(true);

                const response = await apiClient.request(`api/v1${logoPath}`, {}, 'GET', true, true);

                setLogo(response);
            } catch (error) {
                console.error(error);
                message.error(intl.formatMessage({id: 'error.data_load'}));
            } finally {
                setLogoIsLoading(false);
            }
        };

        if (logoPath) {
            loadLogo();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [logoPath]);

    const intl = useIntl();

    const customerOptions = Object.entries(session.user_types.customers || {}).map((el: any) => ({
        value: el[0],
        label: el[1].name,
        roles: el[1].roles
    }));
    const organizationOptions = Object.entries(session.user_types.organizations || {}).map((el: any) => ({
        value: el[0],
        label: el[1].name,
        roles: el[1].roles
    }));

    const switchToStudentRole = () => {
        return roles.map((element: any) => {
            if (element.role === 'STUDENT') {
                setSelectedUserTypeId(element.userTypeId)
            }
            return null;
        })
    }

    const showDrawer = () => {
        setOpen(true);
    };

    const closeDrawer = () => {
        setOpen(false);
    };

    const getSpecialClientFavIcon = () => {
        // For authenticated pages, rely on `appearanceSettings.faviconPath` (loaded as blob).
        // Until it loads, fall back to the app default favicon to avoid showing public-tenant branding.
        return '/favicon.ico';
    }

    return (
        <Spinner spinning={session.loading || languageDictionary.loading || appearanceSettingsLoading} opaque={true}>
            <Helmet>
                {favicon
                    ? <link rel="icon" type="image/png" href={URL.createObjectURL(favicon)} sizes="16x16"/>
                    : <link rel="icon" type="image/png" href={getSpecialClientFavIcon()} sizes="16x16"/>
                }
                <title>{appearanceSettings.title}</title>
            </Helmet>
            <ThemeSwitch theme={appearanceSettings.theme}/>
            <Layout style={{minHeight: '100vh'}}>
                <Header className='site-header'>
                    {environment === 'LEARNING'
                        ?
                        <div className="brand-logo">
                            <NavLink to='/'>
                                <Spin spinning={appearanceSettingsLoading || logoIsLoading} size="small">
                                    {!logoPath
                                        ? <img src="/cs-logo.svg" alt={title}/>
                                        : <img src={logo ? URL.createObjectURL(logo) : undefined} alt={title}/>
                                    }
                                </Spin>
                            </NavLink>
                        </div>
                        : null
                    }

                    {
                        isMobile ?
                            <div className='header-menu-mobile'>
                                <MenuOutlined onClick={showDrawer} style={{fontSize: '24px', color: '#19A9CE'}}/>
                                <Drawer title="Menu" placement="right" onClose={closeDrawer} open={open} width={'100%'}>
                                    <HeaderMenu
                                        selectedOrganization={selectedOrganization}
                                        customerOptions={customerOptions}
                                        organizationOptions={organizationOptions}
                                        setSelectedOrganizationType={setSelectedOrganizationType}
                                        setSelectedOrganization={setSelectedOrganization}
                                        roles={roles}
                                        setSelectedUserTypeId={setSelectedUserTypeId}
                                        environment={environment}
                                        switchToStudentRole={switchToStudentRole}
                                        switchToAdministrationEnvironment={switchToAdministrationEnvironment}
                                        session={session}
                                        isMobile={isMobile}
                                        closeDrawer={closeDrawer}
                                        clearSession={clearSession}
                                    />
                                </Drawer>
                            </div>
                            :
                            <div className='header-menu'>
                                <HeaderMenu
                                    selectedOrganization={selectedOrganization}
                                    customerOptions={customerOptions}
                                    organizationOptions={organizationOptions}
                                    setSelectedOrganizationType={setSelectedOrganizationType}
                                    setSelectedOrganization={setSelectedOrganization}
                                    roles={roles}
                                    setSelectedUserTypeId={setSelectedUserTypeId}
                                    environment={environment}
                                    switchToStudentRole={switchToStudentRole}
                                    switchToAdministrationEnvironment={switchToAdministrationEnvironment}
                                    session={session}
                                    clearSession={clearSession}
                                />
                            </div>
                    }
                </Header>
                {environment === 'ADMINISTRATION'
                    ? <Sidebar collapsed={collapsed} setCollapsed={setCollapsed}/>
                    : window.location.pathname !== '/student/show-activity' && !window.location.pathname.startsWith('/assessment/')
                        ? <StudentNavBar/>
                        : null
                }
                <Content>
                    <Router/>
                </Content>
            </Layout>
            <VideoInstruction
                visible={showVideoInstructionModal}
                onCancel={() => setShowVideoInstructionModal(false)}
                videoCode={code && code}
                setCode={setCode}
            />
            {showRulesModal &&
              <Confirmation />
            }
        </Spinner>
    );
}

DefaultLayout.PageLayout = PageLayout;
DefaultLayout.PageContent = PageContent;
DefaultLayout.PageSidebar = PageSidebar;
DefaultLayout.PageHeader = PageHeader;
DefaultLayout.PageFooter = PageFooter;

export default connect(mapStateToProps, mapDispatchToProps)(DefaultLayout);
